


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import type { StandardReportCreateParams } from '@/models/apis/externalReports/standard/standardReportRequest';
import type {
  StandardReportCar,
  StandardReport,
  SelectOption,
} from '@/models/externalReports/standard/spStandardReport';
import type {
  ExternalReportsLabelSp,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import {
  EXTERNAL_REPORT_CONTENTS_OTHER,
  CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS,
} from '@/consts/externalReports/standard';
import standardReportApi from '@/apis/externalReports/standard/standard_report';
import standardCarApi from '@/apis/externalReports/standard/standard_car';
import useSpeech from '@/composables/useSpeech';
import { parseSelectionOptions } from '@/components/Sp/ExternalReports/Standard/utils';

interface DepartureState {
  hanDisplayName: string;
  selectedContents: SelectOption[];
  selectedRoadNames: SelectOption[];
  otherContent: string;
  showConfirmBaseDepartureModal: boolean;
  standardReport: StandardReport;
}
export default defineComponent({
  props: {
    standardCar: {
      type: Object as PropType<StandardReportCar>,
      required: true,
    },
    externalReportAbility: {
      type: Object as PropType<ExternalReportAbility>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
  },
  emits: ['startMoving', 'showErrorOnCommon'],
  setup(props, { emit }) {
    const state = reactive<DepartureState>({
      hanDisplayName: '',
      selectedContents: [],
      selectedRoadNames: [],
      otherContent: '',
      showConfirmBaseDepartureModal: false,
      standardReport: props.standardCar.report,
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });
    const { doSpeech } = useSpeech();

    const showOtherTypeArea = computed<boolean>(() => {
      return state.selectedContents.some(e => e.name === EXTERNAL_REPORT_CONTENTS_OTHER);
    });

    const workContents = computed<SelectOption[]>(() => {
      return props.userSettings.choices.work_types.map((e, index) => ({ id: index, name: e }));
    });
    const roadNames = computed<SelectOption[]>(() => {
      return props.userSettings.choices.work_routes.map((e, index) => ({ id: index, name: e }));
    });
    const spLabels = computed<ExternalReportsLabelSp>(() => {
      return props.userSettings.sp;
    });
    const selectedContents = computed<string>(() => {
      return state.selectedContents.map(e => e.name).join(CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS);
    });
    const selectedRoadNames = computed<string>(() => {
      return state.selectedRoadNames.map(e => e.name).join(CONNECTION_CHAR_FOR_JOIN_ROADS_CONTENTS);
    });

    const canStart = computed<boolean>(() => {
      return !isRequesting.value && checkInputs.value;
    });

    const checkInputs = computed<boolean>(() => {
      if (!state.hanDisplayName) return false;
      if (state.selectedContents.length === 0) return false;
      if (showOtherTypeArea.value && !state.otherContent) return false;
      if (state.selectedRoadNames.length === 0) return false;
      return true;
    });

    const startMoving = async(): Promise<void> => {
      if (!canStart.value) return;

      state.showConfirmBaseDepartureModal = false;
      await createStandardReport();
      doSpeech(spLabels.value.message.confirm_departure);
      emit('startMoving', props.userSettings.choices.status.running.key, state.standardReport);
    };

    const createStandardReport = async(): Promise<void> => {
      const params: StandardReportCreateParams = {
        external_reports_standard_car_id: props.standardCar.id,
        base_departure_ts: new Date(),
        han_name: state.hanDisplayName,
        contents: selectedContents.value,
        road_names: selectedRoadNames.value,
        other_content: showOtherTypeArea.value ? state.otherContent : '',
      };
      await doRequest(async() => {
        state.standardReport = await standardReportApi.createStandardReport(params);
        await standardCarApi.updateReportId(props.standardCar.id, {
          current_standard_report_id: state.standardReport.id,
        });
      }, '走行開始に失敗しました。再度操作を行ってください');
    };

    onMounted(() => {
      if (!props.externalReportAbility || !props.externalReportAbility.han_name) return;

      state.hanDisplayName = props.externalReportAbility.han_name;
      const report = props.standardCar.report;
      if (!report || !report.contents || !report.roadNames) return;

      state.selectedContents = parseSelectionOptions(
        report.contents,
        props.userSettings.choices.work_types,
      );
      state.selectedRoadNames = parseSelectionOptions(
        report.roadNames,
        props.userSettings.choices.work_routes,
      );
      state.otherContent = report.otherContent || '';
    });

    return {
      state,
      canStart,
      isRequesting,
      roadNames,
      showOtherTypeArea,
      spLabels,
      workContents,
      startMoving,
    };
  },
});
